import RefreshIcon from "@mui/icons-material/CachedRounded";
import { Box, Button, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import { searchApprovals } from "./approvalsFiltering";
import PendingApprovalsGrid from "./PendingApprovalsGrid";

interface Props {
  target: "for-me" | "requested-by-me";
}

const PendingApprovalsList = ({ target }: Props) => {
  const getPendingApprovals = useCallback(
    () => (target === "requested-by-me" ? adminApi.getApprovalsRequestedByUser() : adminApi.getPendingApprovals()),
    [target]
  );

  const [pendingApprovals, fetchError, { fetch }] = useFetch(getPendingApprovals);

  const [searchTerm, setSearchTerm] = useState("");

  if (fetchError) {
    logError(fetchError, "[PendingApprovalsList] getApprovals");
    return <DataLoadingFailed />;
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim().toLocaleLowerCase());
  };

  const loading = pendingApprovals === undefined;

  const approvals = pendingApprovals ?? [];

  const filteredApprovals = searchApprovals(approvals, searchTerm);

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
        <RecordCounter records={filteredApprovals.length} totalRecords={approvals.length} hide={loading} />
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <SearchField debounceTimeMs={300} onSearch={handleSearch} />
          <Button
            variant="outlined"
            color="secondary"
            disabled={loading}
            startIcon={<RefreshIcon />}
            onClick={() => fetch()}
          >
            Refresh
          </Button>
        </Stack>
      </Box>
      <PendingApprovalsGrid rows={filteredApprovals} loading={loading} />
    </Stack>
  );
};

export default PendingApprovalsList;
